/**
 * LabEvent 事件总线。
 *
 * 技术设计 §2 的横切约束（`:129`）：**遥测层只订阅事件总线，不反向调用交互层和逻辑层**。
 * 所以这里只提供「广播 + 订阅」，不提供任何让订阅者回写引擎状态的通道。
 * 这条约束保证：关掉上报，实验照常可跑（§22 红线 18「断网可跑」）。
 *
 * 注意：这是**引擎内部总线**，不是出网通道。事件目录白名单（§18.2，红线 15）在 S0 才建，
 * S0 必须等 P2 完成（§21.3），此时不能凭空设计事件名。
 */

export interface LabEvent<P = unknown> {
  type: string;
  payload: P;
  /** 毫秒时间戳 */
  ts: number;
  /** 单调自增序号，只用于排序（§22 红线 17：sequence 不用于拒收） */
  seq: number;
}

export type LabEventHandler<P = unknown> = (event: LabEvent<P>) => void;

/** 通配订阅：收到所有事件。遥测层将来订的就是这个。 */
export const ANY_EVENT = "*";

export type Unsubscribe = () => void;

export class EventBus {
  private readonly handlers = new Map<string, Set<LabEventHandler>>();
  private seq = 0;

  on<P = unknown>(type: string, handler: LabEventHandler<P>): Unsubscribe {
    const set = this.handlers.get(type) ?? new Set<LabEventHandler>();
    set.add(handler as LabEventHandler);
    this.handlers.set(type, set);
    return () => this.off(type, handler);
  }

  off<P = unknown>(type: string, handler: LabEventHandler<P>): void {
    this.handlers.get(type)?.delete(handler as LabEventHandler);
  }

  emit<P = unknown>(type: string, payload: P): LabEvent<P> {
    const event: LabEvent<P> = { type, payload, ts: Date.now(), seq: ++this.seq };
    this.dispatch(this.handlers.get(type), event);
    this.dispatch(this.handlers.get(ANY_EVENT), event);
    return event;
  }

  /** 单个订阅者抛错不能拖垮其余订阅者，也不能打断正在进行的实验操作。 */
  private dispatch<P>(handlers: Set<LabEventHandler> | undefined, event: LabEvent<P>): void {
    if (!handlers) return;
    for (const handler of [...handlers]) {
      try {
        handler(event as LabEvent);
      } catch (error) {
        console.error(`[LabEngine] 事件 ${event.type} 的订阅者抛出异常：`, error);
      }
    }
  }

  clear(): void {
    this.handlers.clear();
  }

  get lastSeq(): number {
    return this.seq;
  }
}
